import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Store, Plus, Banknote, Edit, Power, RotateCcw, AlertTriangle } from "lucide-react";

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [courts, setCourts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payOpen, setPayOpen] = useState(null);
  const [payForm, setPayForm] = useState({ period_start: "", period_end: "" });
  const [deactivating, setDeactivating] = useState(null);
  const [deactForm, setDeactForm] = useState({ reason: "", effective_date: "" });

  const load = () => {
    api.get("/vendors", { params: { include_inactive: 1 } }).then(r => setVendors(r.data));
    api.get("/vendors/payouts").then(r => setPayouts(r.data)).catch(()=>{});
    api.get("/food-courts").then(r => setCourts(r.data)).catch(()=>setCourts([]));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = { ...editing, settlement_pct: Number(editing.settlement_pct), food_court_id: editing.food_court_id || null };
      if (editing.id) await api.put(`/vendors/${editing.id}`, payload);
      else await api.post("/vendors", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitDeactivate = async () => {
    if (!deactForm.reason.trim()) return toast.error("Please add a reason");
    try {
      await api.patch(`/vendors/${deactivating.id}/deactivate`, deactForm);
      toast.success(`${deactivating.name} deactivated`);
      setDeactivating(null); setDeactForm({ reason: "", effective_date: "" }); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reactivate = async (v) => {
    if (!window.confirm(`Reactivate ${v.name}?`)) return;
    try {
      await api.patch(`/vendors/${v.id}/reactivate`);
      toast.success("Reactivated"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const processPayout = async () => {
    try {
      await api.post(`/vendors/${payOpen.id}/payout`, payForm);
      toast.success("Payout created"); setPayOpen(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const markPaid = async (pid) => {
    await api.patch(`/vendors/payouts/${pid}/mark-paid`);
    toast.success("Marked paid"); load();
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Vendors / Brands</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Multi-brand canteen</h1>
          </div>
          <button data-testid="new-vendor-btn" onClick={() => setEditing({ name:"", brand_logo:"", category:"", contact_phone:"", settlement_pct: 85, status: "active", operator_type: "vendor", gstin: "", counter_label: "", food_court_id: "" })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold">
            <Plus size={16} /> Add vendor
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {vendors.map(v => {
            const court = courts.find(c => c.id === v.food_court_id);
            const isInactive = v.status !== "active";
            return (
            <div key={v.id} className={`bg-white rounded-3xl border p-6 ${isInactive ? "border-red-200 bg-red-50/30" : "border-border"}`} data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isInactive ? "bg-red-100 text-red-700" : "bg-cream-200 text-brand"}`}><Store size={22} /></div>
                  <div>
                    <div className="font-heading text-xl font-bold text-ink">{v.name}</div>
                    <div className="text-xs text-ink-500">{v.category}</div>
                    {court && <div data-testid={`vendor-court-${v.id}`} className="text-[10px] font-bold uppercase tracking-wider text-brand mt-0.5">{court.name}</div>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${v.status==='active'?'bg-emerald-100 text-emerald-900':'bg-red-100 text-red-800'}`}>{v.status}</span>
              </div>
              {isInactive && v.deactivation_reason && (
                <div data-testid={`vendor-deact-${v.id}`} className="mt-3 p-2.5 bg-red-50 rounded-xl border border-red-200 text-[11px] text-red-900 leading-snug">
                  <div className="font-bold flex items-center gap-1"><AlertTriangle size={11}/> Deactivated{v.deactivation_effective_date ? ` from ${v.deactivation_effective_date}` : ""}</div>
                  <div className="mt-1">{v.deactivation_reason}</div>
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-ink-600">
                <div><div className="font-bold text-ink-500 uppercase tracking-wider">Operator</div><div className="font-bold text-ink mt-1 inline-flex items-center gap-1">{v.operator_type === "school" ? <span className="px-1.5 py-0.5 bg-brand text-white rounded text-[9px]">SCHOOL</span> : <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded text-[9px]">VENDOR</span>}</div></div>
                <div><div className="font-bold text-ink-500 uppercase tracking-wider">Settlement</div><div className="font-bold text-ink mt-1">{v.settlement_pct}% / {(100-v.settlement_pct).toFixed(0)}% platform</div></div>
                <div><div className="font-bold text-ink-500 uppercase tracking-wider">Contact</div><div className="font-mono text-ink mt-1">{v.contact_phone || "—"}</div></div>
                <div><div className="font-bold text-ink-500 uppercase tracking-wider">GSTIN</div><div className="font-mono text-ink mt-1 truncate">{v.gstin || "—"}</div></div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                {!isInactive && (
                  <button data-testid={`vendor-payout-${v.id}`} onClick={() => { setPayOpen(v); setPayForm({ period_start: new Date(Date.now()-7*86400000).toISOString().slice(0,10), period_end: new Date().toISOString().slice(0,10) }); }}
                    className="px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold flex items-center gap-1"><Banknote size={12} /> New payout</button>
                )}
                <button onClick={() => setEditing(v)} className="px-3 py-1.5 rounded-full bg-cream-200 text-xs font-semibold flex items-center gap-1"><Edit size={12} /> Edit</button>
                {isInactive ? (
                  <button data-testid={`vendor-reactivate-${v.id}`} onClick={() => reactivate(v)} className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs font-semibold flex items-center gap-1"><RotateCcw size={12} /> Reactivate</button>
                ) : (
                  <button data-testid={`vendor-deactivate-${v.id}`} onClick={() => { setDeactivating(v); setDeactForm({ reason: "", effective_date: new Date().toISOString().slice(0,10) }); }} className="px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold flex items-center gap-1"><Power size={12} /> Deactivate</button>
                )}
              </div>
            </div>
          );})}
        </div>

        <div className="bg-white rounded-xl border border-border overflow-x-auto">
          <div className="p-4 border-b border-border font-heading text-xl font-bold">Payouts</div>
          <table className="w-full text-sm">
            <thead className="bg-cream-200">
              <tr>
                <th className="p-3 text-left text-xs uppercase font-bold">Vendor</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Period</th>
                <th className="p-3 text-right text-xs uppercase font-bold">Orders</th>
                <th className="p-3 text-right text-xs uppercase font-bold">Gross</th>
                <th className="p-3 text-right text-xs uppercase font-bold">Platform fee</th>
                <th className="p-3 text-right text-xs uppercase font-bold">Net</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {payouts.map(p => (
                <tr key={p.id} className="border-t border-border" data-testid={`payout-${p.id}`}>
                  <td className="p-3 font-semibold">{p.vendor_name}</td>
                  <td className="p-3 text-xs">{p.period_start} → {p.period_end}</td>
                  <td className="p-3 text-right">{p.order_count}</td>
                  <td className="p-3 text-right">{inr(p.gross)}</td>
                  <td className="p-3 text-right text-ink-500">{inr(p.platform_fee)}</td>
                  <td className="p-3 text-right font-bold">{inr(p.net)}</td>
                  <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.status==='paid'?'bg-emerald-100 text-emerald-900':'bg-amber-100 text-amber-900'}`}>{p.status}</span></td>
                  <td className="p-3 text-right">
                    {p.status === "pending" && <button data-testid={`mark-paid-${p.id}`} onClick={() => markPaid(p.id)} className="px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-900">Mark paid</button>}
                  </td>
                </tr>
              ))}
              {payouts.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-ink-500">No payouts yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">{editing.id ? "Edit" : "New"} counter / vendor</div>
            <div className="text-xs text-ink-500 mt-1">A "counter" can be school-operated (school staff run it) or vendor-operated (third-party brand).</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Brand / Counter name</label>
                <input data-testid="vendor-form-name" value={editing.name ?? ""} onChange={e => setEditing({...editing, name: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Operator type *</label>
                <select data-testid="vendor-form-operator-type" value={editing.operator_type ?? "vendor"} onChange={e => setEditing({...editing, operator_type: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20">
                  <option value="school">School-operated (school staff run it)</option>
                  <option value="vendor">Vendor-operated (third-party brand)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Food court (optional)</label>
                <select data-testid="vendor-form-food-court" value={editing.food_court_id ?? ""} onChange={e => setEditing({...editing, food_court_id: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20">
                  <option value="">— Single canteen / no court —</option>
                  {courts.map(c => <option key={c.id} value={c.id}>{c.name}{c.location ? ` · ${c.location}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Counter label (optional)</label>
                <input data-testid="vendor-form-counter-label" value={editing.counter_label ?? ""} onChange={e => setEditing({...editing, counter_label: e.target.value})}
                  placeholder="e.g., Main Counter, South Indian Stall"
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Category</label>
                <input data-testid="vendor-form-category" value={editing.category ?? ""} onChange={e => setEditing({...editing, category: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Contact phone</label>
                <input data-testid="vendor-form-contact-phone" value={editing.contact_phone ?? ""} onChange={e => setEditing({...editing, contact_phone: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Vendor share % {editing.operator_type === "school" && <span className="text-amber-700">(unused for school-operated)</span>}</label>
                <input data-testid="vendor-form-settlement-pct" type="number" value={editing.settlement_pct ?? 85} onChange={e => setEditing({...editing, settlement_pct: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">GSTIN {editing.operator_type === "school" ? <span className="text-ink-500">(falls back to school GSTIN if empty)</span> : <span className="text-amber-700">*</span>}</label>
                <input data-testid="vendor-form-gstin" value={editing.gstin ?? ""} onChange={e => setEditing({...editing, gstin: e.target.value.toUpperCase()})}
                  placeholder="e.g., 27AAACS1234Z1ZK"
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 font-mono focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                <div className="text-[10px] text-ink-500 mt-1">Vendor-operated counters issue invoices in vendor's GSTIN. School-operated counters use the school GSTIN.</div>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Logo URL</label>
                <input data-testid="vendor-form-brand-logo" value={editing.brand_logo ?? ""} onChange={e => setEditing({...editing, brand_logo: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="vendor-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}

      {deactivating && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setDeactivating(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="deactivate-modal">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center"><Power size={18}/></div>
              <div>
                <div className="font-heading text-2xl font-bold">Deactivate {deactivating.name}</div>
                <div className="text-xs text-ink-500">The counter will stop appearing in QR/POS and won't accept new orders.</div>
              </div>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Reason <span className="text-brand">*</span></label>
                <textarea data-testid="deact-reason" value={deactForm.reason} onChange={e => setDeactForm({...deactForm, reason: e.target.value})}
                  rows={3} placeholder="e.g., Vendor exit, breach of agreement, refurbishment, end of contract…"
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Effective from</label>
                <input data-testid="deact-date" type="date" value={deactForm.effective_date} onChange={e => setDeactForm({...deactForm, effective_date: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                <div className="text-[10px] text-ink-500 mt-1">Leave today's date to deactivate immediately. Future date keeps the counter live until then.</div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setDeactivating(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="deact-submit" onClick={submitDeactivate} className="px-5 py-2.5 rounded-full bg-red-600 text-white font-semibold">Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {payOpen && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setPayOpen(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">Process payout — {payOpen.name}</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">From</label>
                <input data-testid="payout-from" type="date" value={payForm.period_start} onChange={e => setPayForm({...payForm, period_start: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">To</label>
                <input data-testid="payout-to" type="date" value={payForm.period_end} onChange={e => setPayForm({...payForm, period_end: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPayOpen(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="payout-create" onClick={processPayout} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Create payout</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
